"use client";

import { usePropertySummary } from "@/hooks/audio-summary-hook";
import { AnimatePresence, motion } from "framer-motion";
import { Headphones, Loader2, Pause, Play, Square } from "lucide-react";
import { useLocale } from "next-intl";
import { useEffect, useRef, useState } from "react";

export default function AudioSummary({ slug }: { slug?: string }) {
  const locale = useLocale();
  const isAr = locale === "ar";
  const summaryM = usePropertySummary();

  const [lang, setLang] = useState<"ar" | "en">(isAr ? "ar" : "en");
  const [status, setStatus] = useState<"idle" | "loading" | "playing" | "paused">("idle");
  const cacheRef = useRef<Record<string, string>>({});

  /* preload voices (they load async — empty list = robotic default voice) */
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const load = () => window.speechSynthesis.getVoices();
    load();
    window.speechSynthesis.addEventListener?.("voiceschanged", load);
    return () => {
      window.speechSynthesis.cancel();
      window.speechSynthesis.removeEventListener?.("voiceschanged", load);
    };
  }, []);

  /* pick the most natural available voice for the language */
  const pickVoice = (l: "ar" | "en") => {
    const voices = window.speechSynthesis.getVoices();
    const langCode = l === "ar" ? "ar" : "en";
    const matches = voices.filter((v) => v.lang?.toLowerCase().startsWith(langCode));
    if (!matches.length) return null;

    // prefer high-quality / neural / online voices
    const PREFERRED = [
      "natural", "neural", "online", "premium", "enhanced",
      "google", "microsoft", "siri", "حصة", "حامد", "زارية", "نورا",
    ];
    const scored = matches
      .map((v) => {
        const name = (v.name || "").toLowerCase();
        let score = 0;
        PREFERRED.forEach((p, i) => {
          if (name.includes(p)) score += PREFERRED.length - i + 5;
        });
        if (!v.localService) score += 3; // online voices are usually neural
        if (l === "ar" && v.lang?.toLowerCase().includes("ar-sa")) score += 2;
        return { v, score };
      })
      .sort((a, b) => b.score - a.score);

    return scored[0].v;
  };

  const speak = (text: string, l: "ar" | "en") => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();

    const run = () => {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = l === "ar" ? "ar-SA" : "en-US";
      u.rate = l === "ar" ? 0.92 : 0.95; // slightly slower = more natural
      u.pitch = 1.02;
      u.volume = 1;
      const v = pickVoice(l);
      if (v) u.voice = v;
      u.onend = () => setStatus("idle");
      u.onerror = () => setStatus("idle");
      window.speechSynthesis.speak(u);
      setStatus("playing");
    };

    // if voices not ready yet, wait for them once
    if (window.speechSynthesis.getVoices().length === 0) {
      const once = () => {
        window.speechSynthesis.removeEventListener?.("voiceschanged", once);
        run();
      };
      window.speechSynthesis.addEventListener?.("voiceschanged", once);
      // fallback in case the event never fires
      setTimeout(run, 250);
    } else {
      run();
    }
  };

  const handlePlay = async () => {
    if (!slug) return;

    // resume if paused
    if (status === "paused") {
      window.speechSynthesis.resume();
      setStatus("playing");
      return;
    }
    // pause if playing
    if (status === "playing") {
      window.speechSynthesis.pause();
      setStatus("paused");
      return;
    }

    // start: use cache or fetch
    const cached = cacheRef.current[lang];
    if (cached) {
      speak(cached, lang);
      return;
    }
    setStatus("loading");
    summaryM.mutate(
      { slug, locale: lang },
      {
        onSuccess: (d) => {
          cacheRef.current[lang] = d.summary;
          speak(d.summary, lang);
        },
        onError: () => setStatus("idle"),
      },
    );
  };

  const stop = () => {
    if (typeof window !== "undefined") window.speechSynthesis?.cancel();
    setStatus("idle");
  };

  const switchLang = (l: "ar" | "en") => {
    if (l === lang) return;
    stop();
    setLang(l);
  };

  if (!slug) return null;

  const playing = status === "playing";

  return (
    <div
      dir={isAr ? "rtl" : "ltr"}
      className="my-6 rounded-3xl border border-slate-100 bg-gradient-to-br from-white to-[#0d3589]/[0.03] p-5 shadow-sm flex flex-col sm:flex-row sm:items-center gap-4"
    >
      <div className="flex items-center gap-3 flex-1">
        <div className="size-12 rounded-2xl bg-gradient-to-br from-[#0d3589] to-[#071f52] flex items-center justify-center text-amber-300 shadow-md shrink-0">
          <Headphones size={22} />
        </div>
        <div>
          <h3 className="font-black text-slate-900 text-base">
            {isAr ? "ملخص صوتي للعقار" : "Audio Property Summary"}
          </h3>
          <p className="text-xs text-slate-400">
            {isAr ? "استمع لملخص ذكي عن العقار" : "Listen to an AI summary of this property"}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        {/* language toggle */}
        <div className="flex bg-slate-100 rounded-xl p-1">
          {(["ar", "en"] as const).map((l) => (
            <button
              key={l}
              onClick={() => switchLang(l)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                lang === l ? "bg-white text-[#0d3589] shadow-sm" : "text-slate-400"
              }`}
            >
              {l === "ar" ? "عربي" : "EN"}
            </button>
          ))}
        </div>

        {/* play / pause */}
        <button
          onClick={handlePlay}
          disabled={status === "loading"}
          className="size-12 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-500 text-[#0d3589] flex items-center justify-center shadow-lg shadow-amber-500/30 hover:brightness-105 active:scale-95 transition-all disabled:opacity-60"
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={status}
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
            >
              {status === "loading" ? (
                <Loader2 size={22} className="animate-spin" />
              ) : playing ? (
                <Pause size={22} className="fill-[#0d3589]" />
              ) : (
                <Play size={22} className="fill-[#0d3589]" />
              )}
            </motion.span>
          </AnimatePresence>
        </button>

        {/* stop (only when active) */}
        {status !== "idle" && status !== "loading" && (
          <button
            onClick={stop}
            className="size-10 rounded-xl bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center transition-colors"
            title={isAr ? "إيقاف" : "Stop"}
          >
            <Square size={16} className="fill-slate-500" />
          </button>
        )}
      </div>

      {/* live equalizer bars when playing */}
      {playing && (
        <div className="flex items-end gap-1 h-6 shrink-0">
          {[0, 1, 2, 3, 4].map((i) => (
            <span
              key={i}
              className="w-1 bg-amber-400 rounded-full animate-bounce"
              style={{ height: `${8 + (i % 3) * 6}px`, animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
